import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Code2, BookOpen, Key, Webhook, Terminal, ExternalLink, Rocket } from 'lucide-react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModulePageHeader, ModuleLoadingState, ModuleCard } from '../../shared/components/ModulePageHeader';
import { getDeveloperAccount, getDeveloperApps } from '../../../services/platformService';

const API_ENDPOINTS = [
  { method: 'GET', path: '/api/v1/workflows', description: 'Listar workflows' },
  { method: 'POST', path: '/api/v1/workflows', description: 'Criar workflow' },
  { method: 'GET', path: '/api/v1/actions', description: 'Listar ações' },
  { method: 'POST', path: '/api/v1/actions', description: 'Criar ação' },
  { method: 'GET', path: '/api/v1/users', description: 'Listar usuários' },
  { method: 'GET', path: '/api/v1/analytics', description: 'Métricas e analytics' },
  { method: 'GET', path: '/api/v1/templates', description: 'Listar templates' },
];

const SDK_LANGUAGES = [
  { lang: 'TypeScript', icon: '🟦', code: `import { WorkflowSDK } from '@workflow/sdk';\n\nconst sdk = new WorkflowSDK({ apiKey: 'wf_sk_...' });\nconst workflows = await sdk.getWorkflows();` },
  { lang: 'JavaScript', icon: '🟨', code: `const { WorkflowSDK } = require('@workflow/sdk');\n\nconst sdk = new WorkflowSDK({ apiKey: 'wf_sk_...' });\nconst workflows = await sdk.getWorkflows();` },
  { lang: 'Python', icon: '🐍', code: `from workflow_sdk import WorkflowSDK\n\nsdk = WorkflowSDK(api_key='wf_sk_...')\nworkflows = sdk.get_workflows()` },
];

const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
  const colors: Record<string, string> = {
    GET: 'bg-emerald-500/10 text-emerald-600',
    POST: 'bg-blue-500/10 text-blue-600',
    PUT: 'bg-amber-500/10 text-amber-600',
    DELETE: 'bg-red-500/10 text-red-600',
  };
  return (
    <span className={`inline-block w-12 text-center px-1.5 py-0.5 text-[10px] font-mono font-bold rounded ${colors[method] || 'bg-gray-500/10 text-gray-600'}`}>
      {method}
    </span>
  );
};

export const AdminDeveloperPortalPage: React.FC = () => {
  const devAccountQuery = useQuery({
    queryKey: ['developer-account'],
    queryFn: getDeveloperAccount,
  });

  const devAppsQuery = useQuery({
    queryKey: ['developer-apps'],
    queryFn: getDeveloperApps,
    enabled: !!devAccountQuery.data,
  });

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">
          <ModulePageHeader icon={Code2} title="Developer Portal" subtitle="APIs, SDKs e documentação para desenvolvedores"
            rightContent={
              <span className="text-[11px] px-2.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 font-medium">
                Em breve
              </span>
            }
          />

          <div className="px-6 pb-8 space-y-6">

            {/* Quick Links */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: BookOpen, label: 'API Docs', desc: 'Documentação completa' },
                { icon: Terminal, label: 'SDK', desc: 'Download SDKs' },
                { icon: Key, label: 'Credenciais', desc: 'API Keys e OAuth' },
                { icon: Webhook, label: 'Webhooks', desc: 'Configurar eventos' },
              ].map(({ icon: Icon, label, desc }) => (
                <ModuleCard key={label}>
                  <div className="flex items-center gap-3 p-1">
                    <div className="w-9 h-9 rounded bg-[var(--bg-elevated)] flex items-center justify-center">
                      <Icon size={18} className="text-[var(--enterprise-primary,#2563eb)]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[var(--text-primary)]">{label}</p>
                      <p className="text-[11px] text-[var(--text-tertiary)]">{desc}</p>
                    </div>
                  </div>
                </ModuleCard>
              ))}
            </div>

            {/* Public API Reference */}
            <ModuleCard>
              <div className="p-2">
                <div className="flex items-center gap-2 mb-4">
                  <Rocket size={16} className="text-[var(--enterprise-primary,#2563eb)]" />
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">Public API v1</h2>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-600 font-medium">Base: /api/v1</span>
                </div>
                <div className="space-y-1">
                  {API_ENDPOINTS.map(({ method, path, description }) => (
                    <div key={`${method}-${path}`} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-[var(--bg-elevated)] transition-colors">
                      <MethodBadge method={method} />
                      <code className="text-xs font-mono text-[var(--text-primary)] flex-1">{path}</code>
                      <span className="text-[11px] text-[var(--text-tertiary)]">{description}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--border-default)]">
                  <p className="text-[11px] text-[var(--text-tertiary)]">
                    Autenticação: <code className="px-1 bg-[var(--bg-elevated)] rounded">Authorization: Bearer wf_sk_...</code> ou OAuth 2.0.
                    Rate limit: 1000 req/min (padrão).
                  </p>
                </div>
              </div>
            </ModuleCard>

            {/* SDK Examples */}
            <ModuleCard>
              <div className="p-2">
                <div className="flex items-center gap-2 mb-4">
                  <Terminal size={16} className="text-[var(--enterprise-primary,#2563eb)]" />
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">SDK Quick Start</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {SDK_LANGUAGES.map(({ lang, icon, code }) => (
                    <div key={lang} className="border border-[var(--border-default)] rounded overflow-hidden">
                      <div className="px-3 py-1.5 bg-[var(--bg-elevated)] border-b border-[var(--border-default)] flex items-center gap-2">
                        <span>{icon}</span>
                        <span className="text-xs font-medium text-[var(--text-primary)]">{lang}</span>
                      </div>
                      <pre className="p-3 text-[11px] font-mono text-[var(--text-secondary)] overflow-x-auto whitespace-pre leading-relaxed">
                        {code}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            </ModuleCard>

            {/* Webhooks Events */}
            <ModuleCard>
              <div className="p-2">
                <div className="flex items-center gap-2 mb-4">
                  <Webhook size={16} className="text-[var(--enterprise-primary,#2563eb)]" />
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">Eventos Disponíveis</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    'workflow.created', 'workflow.completed', 'action.created',
                    'action.completed', 'user.invited', 'subscription.updated',
                    'invoice.generated', 'app.installed', 'app.uninstalled',
                  ].map((event) => (
                    <div key={event} className="flex items-center gap-2 px-2 py-1.5 rounded bg-[var(--bg-elevated)]">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <code className="text-[11px] font-mono text-[var(--text-secondary)]">{event}</code>
                    </div>
                  ))}
                </div>
              </div>
            </ModuleCard>

            {/* App Publishing Guide */}
            <ModuleCard>
              <div className="p-2">
                <div className="flex items-center gap-2 mb-4">
                  <Rocket size={16} className="text-[var(--enterprise-primary,#2563eb)]" />
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">Publicar um App</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {[
                    { step: '1', title: 'Criar conta', desc: 'Registre-se como developer' },
                    { step: '2', title: 'Desenvolver', desc: 'Use o SDK para criar seu app' },
                    { step: '3', title: 'Submeter', desc: 'Envie para revisão de segurança' },
                    { step: '4', title: 'Publicar', desc: 'App disponível no marketplace' },
                  ].map(({ step, title, desc }) => (
                    <div key={step} className="flex items-start gap-3 p-3 rounded border border-[var(--border-default)]">
                      <div className="w-7 h-7 rounded-full bg-[var(--enterprise-primary,#2563eb)] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {step}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[var(--text-primary)]">{title}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ModuleCard>

            {/* Rate Limits */}
            <ModuleCard>
              <div className="p-2">
                <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Rate Limits</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[var(--text-tertiary)] border-b border-[var(--border-default)]">
                        <th className="pb-2 font-medium">Plano</th>
                        <th className="pb-2 font-medium">Rate Limit</th>
                        <th className="pb-2 font-medium">Burst</th>
                        <th className="pb-2 font-medium">Webhooks</th>
                      </tr>
                    </thead>
                    <tbody className="text-[var(--text-secondary)]">
                      {[
                        { plan: 'Free', rate: '100/min', burst: '10/sec', webhooks: '2' },
                        { plan: 'Starter', rate: '500/min', burst: '25/sec', webhooks: '5' },
                        { plan: 'Pro', rate: '1000/min', burst: '50/sec', webhooks: '20' },
                        { plan: 'Enterprise', rate: '5000/min', burst: '200/sec', webhooks: 'Ilimitado' },
                      ].map(({ plan, rate, burst, webhooks }) => (
                        <tr key={plan} className="border-b border-[var(--border-default)] last:border-0">
                          <td className="py-2 font-medium text-[var(--text-primary)]">{plan}</td>
                          <td className="py-2">{rate}</td>
                          <td className="py-2">{burst}</td>
                          <td className="py-2">{webhooks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ModuleCard>
          </div>
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
